import { PropertyFilters, PropertyPurpose, PropertySearchParams } from '../types/property';

type FilterOption = { value: string; label: string };

const defaultCityOption: FilterOption = { value: '', label: 'Cidade' };
const defaultNeighborhoodOption: FilterOption = { value: '', label: 'Bairro' };
const fallbackCities: FilterOption[] = [
  { value: '', label: 'Cidade' },
  { value: 'sao-paulo', label: 'São Paulo' },
];

const fallbackNeighborhoods: FilterOption[] = [
  { value: '', label: 'Bairro' },
  { value: 'chacara-flora', label: 'Chácara Flora' },
  { value: 'morumbi', label: 'Morumbi' },
  { value: 'alto-da-boa-vista', label: 'Alto da Boa Vista' },
  { value: 'panamby', label: 'Panamby' },
];

export let cityOptions: FilterOption[] = fallbackCities;
export let neighborhoodOptions: FilterOption[] = fallbackNeighborhoods;

export const typeOptions: FilterOption[] = [
  { value: '', label: 'Tipo de imóvel' },
  { value: 'casa', label: 'Casa' },
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'cobertura', label: 'Cobertura' },
  { value: 'casa-condominio', label: 'Casa em Condomínio' },
  { value: 'comercial', label: 'Comercial' },
];

export function slugifyFilterValue(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function setLocationFilterOptions(options: { cities: string[]; neighborhoods: string[] }) {
  cityOptions = mergeFilterOptions(defaultCityOption, options.cities, fallbackCities.slice(1));
  neighborhoodOptions = mergeFilterOptions(defaultNeighborhoodOption, options.neighborhoods, fallbackNeighborhoods.slice(1));
}

export function parseFiltersFromSearchParams(searchParams: URLSearchParams): PropertyFilters {
  const transactionParam = searchParams.get('transaction');
  const transaction =
    transactionParam === 'venda' || transactionParam === 'aluguel'
      ? transactionParam
      : undefined;

  const purposeParam = searchParams.get('purpose');
  const purpose: PropertyPurpose | undefined =
    purposeParam === 'residencial' || purposeParam === 'comercial'
      ? purposeParam
      : undefined;

  const typesParam = searchParams.getAll('types');
  const types = typesParam.length > 0 ? typesParam : undefined;

  return {
    transaction,
    code: searchParams.get('code') || undefined,
    city: normalizeFilterValue(cityOptions, searchParams.get('city')),
    neighborhood: normalizeFilterValue(neighborhoodOptions, searchParams.get('neighborhood')),
    type: searchParams.get('type') || undefined,
    types,
    purpose,
    bedrooms: parsePositiveInt(searchParams.get('bedrooms')),
    suites: parsePositiveInt(searchParams.get('suites')),
    parkingSpaces: parsePositiveInt(searchParams.get('parkingSpaces')),
    priceMin: parsePositiveInt(searchParams.get('priceMin')),
    priceMax: parsePositiveInt(searchParams.get('priceMax')),
  };
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.floor(parsed);
}

export function buildSearchParamsFromFilters(filters: PropertyFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.transaction) {
    params.set('transaction', filters.transaction);
  }

  if (filters.code?.trim()) {
    params.set('code', filters.code.trim());
  }

  if (filters.city) {
    params.set('city', slugifyFilterValue(filters.city));
  }

  if (filters.neighborhood) {
    params.set('neighborhood', slugifyFilterValue(filters.neighborhood));
  }

  if (filters.type) {
    params.set('type', filters.type);
  }

  if (filters.types && filters.types.length > 0) {
    for (const type of filters.types) {
      params.append('types', type);
    }
  }

  if (filters.purpose) {
    params.set('purpose', filters.purpose);
  }

  if (filters.bedrooms && filters.bedrooms > 0) {
    params.set('bedrooms', String(filters.bedrooms));
  }

  if (filters.suites && filters.suites > 0) {
    params.set('suites', String(filters.suites));
  }

  if (filters.parkingSpaces && filters.parkingSpaces > 0) {
    params.set('parkingSpaces', String(filters.parkingSpaces));
  }

  if (filters.priceMin && filters.priceMin > 0) {
    params.set('priceMin', String(filters.priceMin));
  }

  if (filters.priceMax && filters.priceMax > 0) {
    params.set('priceMax', String(filters.priceMax));
  }

  return params;
}

export function buildApiSearchParams(
  filters: PropertyFilters,
  page?: number,
  pageSize?: number
): PropertySearchParams {
  const params: PropertySearchParams = {
    transaction: filters.transaction,
    code: filters.code?.trim() || undefined,
    city: filters.city?.trim() || undefined,
    neighborhood: filters.neighborhood?.trim() || undefined,
    type: filters.type || undefined,
    types: filters.types && filters.types.length > 0 ? filters.types : undefined,
    purpose: filters.purpose,
    bedrooms: filters.bedrooms && filters.bedrooms > 0 ? filters.bedrooms : undefined,
    suites: filters.suites && filters.suites > 0 ? filters.suites : undefined,
    parkingSpaces:
      filters.parkingSpaces && filters.parkingSpaces > 0 ? filters.parkingSpaces : undefined,
    priceMin: filters.priceMin && filters.priceMin > 0 ? filters.priceMin : undefined,
    priceMax: filters.priceMax && filters.priceMax > 0 ? filters.priceMax : undefined,
  };

  if (page) {
    params.page = page;
  }

  if (pageSize) {
    params.pageSize = pageSize;
  }

  return params;
}

function getOptionLabel(options: FilterOption[], value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const match = options.find((option) => option.value === value);
  return match?.label || value;
}

function mergeFilterOptions(
  defaultOption: FilterOption,
  dynamicLabels: string[],
  fallbackOptions: FilterOption[]
): FilterOption[] {
  const optionsByValue = new Map<string, FilterOption>();

  for (const option of fallbackOptions) {
    optionsByValue.set(option.value, option);
  }

  for (const label of dynamicLabels) {
    const normalizedLabel = label.trim();
    if (!normalizedLabel) {
      continue;
    }

    optionsByValue.set(slugifyFilterValue(normalizedLabel), {
      value: slugifyFilterValue(normalizedLabel),
      label: normalizedLabel,
    });
  }

  return [
    defaultOption,
    ...[...optionsByValue.values()].sort((left, right) =>
      left.label.localeCompare(right.label, 'pt-BR', { sensitivity: 'base' })
    ),
  ];
}

function normalizeFilterValue(options: FilterOption[], value: string | null): string | undefined {
  if (!value) {
    return undefined;
  }

  const normalizedValue = value.trim();
  const valueMatch = options.find((option) => option.value === normalizedValue);
  if (valueMatch) {
    return valueMatch.label;
  }

  const labelMatch = options.find(
    (option) =>
      option.label.localeCompare(normalizedValue, 'pt-BR', { sensitivity: 'base' }) === 0 ||
      option.value === slugifyFilterValue(normalizedValue)
  );
  if (labelMatch) {
    return labelMatch.label;
  }

  return normalizedValue;
}
